import React, { useState } from 'react'
import DatePicker from 'react-datepicker'
import { DateFilter, DateFilterPreset, CustomDateRange } from '../../types'
import "react-datepicker/dist/react-datepicker.css"

interface DateFilterComponentProps {
  value: DateFilter
  onChange: (dateFilter: DateFilter) => void
  disabled?: boolean
  disabledMessage?: string
}

const DATE_PRESET_OPTIONS: { value: DateFilterPreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'last-7-days', label: 'Last 7 Days' },
  { value: 'last-30-days', label: 'Last 30 Days' },
  { value: 'this-year', label: 'This Year' },
  { value: 'all-time', label: 'All Time' },
  { value: 'custom', label: 'Custom Range' },
]

export const DateFilterComponent: React.FC<DateFilterComponentProps> = ({ 
  value, 
  onChange, 
  disabled = false, 
  disabledMessage 
}) => {
  const [showCustomPicker, setShowCustomPicker] = useState(value.preset === 'custom')

  const handlePresetChange = (preset: DateFilterPreset) => {
    const newFilter: DateFilter = { preset }
    
    if (preset === 'custom') {
      setShowCustomPicker(true)
      // Keep existing custom range if available, otherwise use sensible defaults
      newFilter.customRange = value.customRange || {
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
        endDate: new Date() // today
      }
    } else {
      setShowCustomPicker(false)
      // Clear custom range for preset options
      newFilter.customRange = undefined
    }
    
    onChange(newFilter)
  }

  const handleCustomRangeChange = (dates: [Date | null, Date | null]) => {
    const [startDate, endDate] = dates
    const customRange: CustomDateRange = { startDate, endDate }
    
    onChange({
      preset: 'custom',
      customRange
    })
  }

  const formatCustomRangeDisplay = (): string => {
    if (!value.customRange?.startDate) return 'Select dates...'
    
    const start = value.customRange.startDate.toLocaleDateString()
    const end = value.customRange.endDate ? 
      value.customRange.endDate.toLocaleDateString() : 
      'Select end date'
    
    return `${start} - ${end}`
  }

  return (
    <div className="space-y-3">
      {/* Disabled Warning */}
      {disabled && disabledMessage && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-amber-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-amber-800">{disabledMessage}</p>
          </div>
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date Range
        </label>
        <select
          value={value.preset}
          onChange={(e) => handlePresetChange(e.target.value as DateFilterPreset)}
          disabled={disabled}
          className={`w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
          }`}
        >
          {DATE_PRESET_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {showCustomPicker && value.preset === 'custom' && !disabled && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Date Range
          </label>
          <div className="relative">
            <DatePicker
              selectsRange
              startDate={value.customRange?.startDate}
              endDate={value.customRange?.endDate}
              onChange={handleCustomRangeChange}
              placeholderText="Select date range"
              className="w-full rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
              dateFormat="MMM d, yyyy"
              maxDate={new Date()} // Don't allow future dates
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              isClearable
            />
          </div>
          {value.customRange?.startDate && (
            <div className="mt-2 text-sm text-gray-600">
              Selected: {formatCustomRangeDisplay()}
            </div>
          )}
        </div>
      )}
    </div>
  )
} 